import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Users, Calendar, CheckCircle, XCircle, AlertCircle, 
  DollarSign, TrendingUp, Building2 
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HRDashboard = () => {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/hr/dashboard`, {
        headers: getAuthHeader()
      });
      setDashboard(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching HR dashboard:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleQuickApprove = async (leaveId) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/employee/hr/leave-requests/${leaveId}/approve`,
        null,
        { headers: getAuthHeader() }
      );
      alert('Leave approved successfully!');
      fetchDashboard();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to approve leave');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64">Loading dashboard...</div>;
  if (!dashboard) return <div className="text-center py-12 text-red-600">Error loading dashboard</div>;

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <Card className="bg-gradient-to-r from-primary to-secondary text-white">
        <CardContent className="p-6">
          <h1 className="text-3xl font-bold mb-2">HR Dashboard</h1>
          <p className="text-white/90">Comprehensive overview of all HR operations</p>
        </CardContent>
      </Card>

      {/* Key Metrics */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/employee/hr/employees')}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-gray-600">Total Employees</CardTitle>
              <Users className="text-primary" size={20} />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-primary">{dashboard.total_employees}</div>
            <p className="text-xs text-gray-500 mt-1">Active employees</p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/employee/hr/leaves')}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-gray-600">Pending Leaves</CardTitle>
              <Calendar className="text-yellow-600" size={20} />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-yellow-600">{dashboard.pending_leaves}</div>
            <p className="text-xs text-gray-500 mt-1">Awaiting approval</p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-gray-600">Today's Attendance</CardTitle>
              <CheckCircle className="text-green-600" size={20} />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">
              {dashboard.attendance_today.present}/{dashboard.attendance_today.total}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {dashboard.attendance_today.absent} absent
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/employee/hr/payroll')}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-gray-600">Payroll Status</CardTitle>
              <DollarSign className="text-blue-600" size={20} />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">
              {dashboard.payroll.generated}/{dashboard.total_employees}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {dashboard.payroll.month} payslips
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Department Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 size={20} />
            Department Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            {Object.entries(dashboard.department_breakdown).map(([dept, count]) => (
              <div key={dept} className="flex items-center justify-between p-3 border rounded-lg">
                <span className="font-medium">{dept}</span>
                <Badge variant="outline" className="bg-primary/10 text-primary border-primary">
                  {count} {count === 1 ? 'employee' : 'employees'}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Pending Leave Requests - Quick Actions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="text-yellow-600" size={20} />
              Pending Leave Approvals
            </CardTitle>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/employee/hr/leaves')}
            >
              View All
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {dashboard.pending_leave_details && dashboard.pending_leave_details.length > 0 ? (
            <div className="space-y-3">
              {dashboard.pending_leave_details.map((leave) => (
                <div key={leave.id} className="flex items-center justify-between p-4 border rounded-lg bg-yellow-50">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-bold">{leave.employee_name}</span>
                      <span className="text-sm text-gray-600">({leave.employee_id})</span>
                    </div>
                    <div className="text-sm text-gray-700">
                      {leave.leave_type.charAt(0).toUpperCase() + leave.leave_type.slice(1)} Leave • {leave.days} days
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(leave.from_date).toLocaleDateString()} - {new Date(leave.to_date).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleQuickApprove(leave.id)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle size={16} className="mr-1" />
                      Quick Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate('/employee/hr/leaves')}
                    >
                      Review
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No pending leave requests</p>
          )}
        </CardContent>
      </Card>

      {/* Recent Announcements */}
      {dashboard.recent_announcements && dashboard.recent_announcements.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Recent Announcements</CardTitle>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/employee/hr/announcements')}
              >
                Manage All
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {dashboard.recent_announcements.map((announcement) => (
                <div key={announcement.id} className="p-4 border rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold">{announcement.title}</h4>
                    <Badge className={
                      announcement.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                      announcement.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                      'bg-blue-100 text-blue-800'
                    }>
                      {announcement.priority.toUpperCase()}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-700">{announcement.content}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    By {announcement.created_by} • {new Date(announcement.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-4 gap-3">
            <Button 
              className="h-20 flex-col gap-2"
              onClick={() => navigate('/employee/hr/employees')}
            >
              <Users size={24} />
              <span className="text-sm">Manage Employees</span>
            </Button>
            <Button 
              className="h-20 flex-col gap-2"
              onClick={() => navigate('/employee/hr/payroll')}
            >
              <DollarSign size={24} />
              <span className="text-sm">Generate Payroll</span>
            </Button>
            <Button 
              className="h-20 flex-col gap-2"
              onClick={() => navigate('/employee/hr/attendance')}
            >
              <Calendar size={24} />
              <span className="text-sm">Attendance Report</span>
            </Button>
            <Button 
              className="h-20 flex-col gap-2"
              onClick={() => navigate('/employee/hr/applications')}
            >
              <TrendingUp size={24} />
              <span className="text-sm">Job Applications</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HRDashboard;
